import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../auth/firebase';
import { apiCall } from '../../api/client';
import { PhoneInput } from '../../components/common/PhoneInput';
import { TimePicker } from '../../components/common/TimePicker';
import { to12h, to24h } from '../../utils/time';
import { resizeImage } from '../../utils/resizeImage';
import { SCHED_DAYS, WEEKDAYS } from '../../constants';
import { SmsConsentDisclosure } from '../../components/common/SmsConsentDisclosure';
import { SMS_CONSENT_SCRIPT_PROVIDER } from '../../constants';
import type { PracticeData, SchedulingWindow } from '../../types';

type OnboardingStep = 'practice' | 'scheduling' | 'provider';

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = (searchParams.get('step') as OnboardingStep) || 'provider';
  const isNewOwner = currentStep === 'practice' || currentStep === 'scheduling';

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Practice setup state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [brandColor, setBrandColor] = useState('#2563EB');
  // Scheduling state
  const [schedWindows, setSchedWindows] = useState<Record<string, { enabled: boolean; startHour: number; startMin: string; startAmpm: string; endHour: number; endMin: string; endAmpm: string }>>(() => {
    const init: Record<string, { enabled: boolean; startHour: number; startMin: string; startAmpm: string; endHour: number; endMin: string; endAmpm: string }> = {};
    for (const day of SCHED_DAYS) {
      const s = to12h('09:00');
      const e = to12h('17:00');
      init[day.key] = {
        enabled: WEEKDAYS.includes(day.key),
        startHour: s.hour12, startMin: s.minute, startAmpm: s.ampm,
        endHour: e.hour12, endMin: e.minute, endAmpm: e.ampm,
      };
    }
    return init;
  });
  const [visitDuration, setVisitDuration] = useState('30');
  const [timezone, setTimezone] = useState('America/New_York');

  // Provider setup state
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [headshotFile, setHeadshotFile] = useState<File | null>(null);
  const [headshotPreviewUrl, setHeadshotPreviewUrl] = useState('');

  const stepLabels: Record<string, string> = {
    practice: isNewOwner ? 'Step 1 of 4' : '',
    scheduling: 'Step 2 of 4',
    provider: isNewOwner ? 'Step 3 of 4' : 'Step 1 of 1',
  };

  const handleLogoFile = useCallback((file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) { setError('Please upload a PNG, JPG, or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Logo must be under 2MB.'); return; }
    setError('');
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  }, []);

  const handleHeadshotFile = useCallback((file: File) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) { setError('Please upload a PNG, JPG, or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Headshot must be under 2MB.'); return; }
    setError('');
    setHeadshotFile(file);
    setHeadshotPreviewUrl(URL.createObjectURL(file));
  }, []);

  // Practice setup save
  const savePracticeSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const updates: Record<string, unknown> = { primaryColor: brandColor };
      if (logoFile) {
        const practiceData = await apiCall<PracticeData>('/practices/mine');
        const resized = await resizeImage(logoFile);
        const ext = resized.type === 'image/png' ? 'png' : 'jpg';
        const storageRef = ref(storage, `practices/${practiceData.practiceId}/logo/logo.${ext}`);
        await uploadBytes(storageRef, resized, { contentType: resized.type });
        updates.logoUrl = await getDownloadURL(storageRef);
      }
      await apiCall('/practices/branding', { method: 'PUT', body: updates });
      setSearchParams({ step: 'scheduling' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // Scheduling save
  const saveScheduling = async () => {
    setError('');
    const windows: Record<string, SchedulingWindow> = {};
    for (const day of SCHED_DAYS) {
      const w = schedWindows[day.key];
      const start = to24h(w.startHour, w.startMin, w.startAmpm);
      const end = to24h(w.endHour, w.endMin, w.endAmpm);
      if (w.enabled && start >= end) {
        setError(`${day.label}: end time must be after start time.`);
        return;
      }
      windows[day.key] = { enabled: w.enabled, start, end };
    }
    setLoading(true);
    try {
      await apiCall('/practices/settings', {
        method: 'PUT',
        body: {
          schedulingWindows: windows,
          defaultVisitDuration: parseInt(visitDuration),
          timezone,
        },
      });
      setSearchParams({ step: 'provider' });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  // Provider save
  const saveProvider = async () => {
    setError('');
    if (!phone) { setError('Cell phone number is required'); return; }
    if (!smsConsent) { setError('SMS consent is required to receive visit notifications'); return; }
    setLoading(true);
    try {
      const practiceData = await apiCall<PracticeData>('/practices/mine');
      const uid = practiceData.provider.uid!;
      const practiceId = practiceData.practiceId;

      let headshotUrl: string | null = null;
      if (headshotFile) {
        const resized = await resizeImage(headshotFile);
        const ext = resized.type === 'image/png' ? 'png' : 'jpg';
        const storageRef = ref(storage, `practices/${practiceId}/headshots/${uid}.${ext}`);
        await uploadBytes(storageRef, resized, { contentType: resized.type });
        headshotUrl = await getDownloadURL(storageRef);
      }

      const profileBody: Record<string, unknown> = {
        name: practiceData.provider.name,
        practiceName: practiceData.practice.branding?.practiceName || practiceData.practice.name || 'My Practice',
        phone,
        smsConsent,
      };
      if (headshotUrl) profileBody.headshotUrl = headshotUrl;

      await apiCall('/practices/profile', { method: 'PUT', body: profileBody });

      // New owners go to billing checkout
      if (isNewOwner || currentStep === 'provider' && searchParams.get('step') === 'provider' && searchParams.get('owner') === '1') {
        try {
          const data = await apiCall<{ url: string }>('/billing/checkout', { method: 'POST' });
          window.location.href = data.url;
          return;
        } catch {
          navigate('/dashboard');
          return;
        }
      }
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const updateSchedDay = (dayKey: string, field: string, value: unknown) => {
    setSchedWindows((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  // Practice setup step
  if (currentStep === 'practice') {
    return (
      <div className="container">
        <div className="card">
          <p className="text-muted text-sm" style={{ marginBottom: '0.25rem' }}>{stepLabels.practice}</p>
          <h2 className="mb-1">Practice Setup</h2>
          <p className="text-muted text-sm mb-2">Upload your practice logo and choose your brand color.</p>
          {error && <div className="alert alert--error">{error}</div>}

          <div className="form-group">
            <label>Practice Logo</label>
            <div
              style={{ border: '2px dashed var(--border-color)', borderRadius: 'var(--radius)', padding: '2rem', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => document.getElementById('logoFileInput')?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) handleLogoFile(e.dataTransfer.files[0]); }}
            >
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} style={{ maxWidth: '200px', maxHeight: '100px', margin: '0 auto', display: 'block' }} alt="Logo preview" />
              ) : (
                <>
                  <p className="text-muted text-sm">Drag & drop your logo here, or click to browse</p>
                  <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>PNG, JPG, or WebP. Min 200x50px, max 2MB.</p>
                </>
              )}
              <input type="file" id="logoFileInput" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleLogoFile(e.target.files[0]); }} />
            </div>
            {logoPreviewUrl && (
              <button className="btn btn--outline btn--sm mt-1" onClick={() => { setLogoFile(null); setLogoPreviewUrl(''); }}>Remove</button>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="brandColorInput">Brand Color</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input type="color" id="brandColorInput" value={brandColor} onChange={(e) => setBrandColor(e.target.value)} style={{ width: '48px', height: '36px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius)', padding: '2px', cursor: 'pointer' }} />
              <span className="text-sm text-muted">{brandColor.toUpperCase()}</span>
            </div>
          </div>

          <button className="btn btn--primary btn--full mt-2" onClick={savePracticeSetup} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Continue</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // Scheduling step
  if (currentStep === 'scheduling') {
    return (
      <div className="container">
        <div className="card">
          <p className="text-muted text-sm" style={{ marginBottom: '0.25rem' }}>{stepLabels.scheduling}</p>
          <h2 className="mb-1">Scheduling</h2>
          <p className="text-muted text-sm mb-2">Set when your practice accepts telehealth visits.</p>
          {error && <div className="alert alert--error">{error}</div>}

          <div className="scheduling-grid">
            {SCHED_DAYS.map((day) => {
              const w = schedWindows[day.key];
              return (
                <div key={day.key} className={`scheduling-day${!w.enabled ? ' scheduling-day--disabled' : ''}`} data-day={day.key}>
                  <label className="scheduling-day__toggle">
                    <input type="checkbox" className="sched-enabled" checked={w.enabled} onChange={(e) => updateSchedDay(day.key, 'enabled', e.target.checked)} />
                    <span className="scheduling-day__label">{day.label}</span>
                  </label>
                  <div className="scheduling-day__times">
                    <TimePicker
                      hour={w.startHour} minute={w.startMin} ampm={w.startAmpm}
                      onHourChange={(h) => updateSchedDay(day.key, 'startHour', h)}
                      onMinuteChange={(m) => updateSchedDay(day.key, 'startMin', m)}
                      onAmpmChange={(a) => updateSchedDay(day.key, 'startAmpm', a)}
                      disabled={!w.enabled}
                    />
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>–</span>
                    <TimePicker
                      hour={w.endHour} minute={w.endMin} ampm={w.endAmpm}
                      onHourChange={(h) => updateSchedDay(day.key, 'endHour', h)}
                      onMinuteChange={(m) => updateSchedDay(day.key, 'endMin', m)}
                      onAmpmChange={(a) => updateSchedDay(day.key, 'endAmpm', a)}
                      disabled={!w.enabled}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '1.25rem 0' }} />

          <div className="form-group">
            <label htmlFor="onboardDuration">Visit Duration</label>
            <select id="onboardDuration" value={visitDuration} onChange={(e) => setVisitDuration(e.target.value)}>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="45">45 minutes</option>
              <option value="60">60 minutes</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="onboardTimezone">Timezone</label>
            <select id="onboardTimezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
              <option value="America/New_York">Eastern (ET)</option>
              <option value="America/Chicago">Central (CT)</option>
              <option value="America/Denver">Mountain (MT)</option>
              <option value="America/Los_Angeles">Pacific (PT)</option>
              <option value="America/Anchorage">Alaska (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii (HT)</option>
            </select>
          </div>

          <button className="btn btn--primary btn--full mt-2" onClick={saveScheduling} disabled={loading}>
            {loading ? <><span style={{ visibility: 'hidden' }}>Continue</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // Provider setup step
  return (
    <div className="container">
      <div className="card">
        <p className="text-muted text-sm" style={{ marginBottom: '0.25rem' }}>{stepLabels.provider}</p>
        <h2 className="mb-1">Provider Setup</h2>
        <p className="text-muted text-sm mb-2">Add a headshot and phone number so patients know who they'll be meeting with.</p>
        {error && <div className="alert alert--error">{error}</div>}

        <div className="form-group">
          <label htmlFor="providerPhone">Cell Phone <span style={{ color: 'var(--error-color)' }}>*</span></label>
          <PhoneInput id="providerPhone" value={phone} onChange={setPhone} />
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal', marginBottom: '1rem' }}>
          <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} style={{ marginTop: '0.2rem', flexShrink: 0, width: '16px', height: '16px' }} />
          <span className="text-sm text-muted">
            I agree to receive SMS visit reminders and notifications at this number. Message and data rates may apply. You can opt out at any time. <span style={{ color: 'var(--error-color)' }}>*</span> <SmsConsentDisclosure intro="Full SMS consent disclosure:" script={SMS_CONSENT_SCRIPT_PROVIDER} />
          </span>
        </label>

        <div className="form-group">
          <label>Your Headshot</label>
          <div
            style={{ border: '2px dashed var(--border-color)', borderRadius: 'var(--radius)', padding: '2rem', textAlign: 'center', cursor: 'pointer' }}
            onClick={() => document.getElementById('headshotFileInput')?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) handleHeadshotFile(e.dataTransfer.files[0]); }}
          >
            {headshotPreviewUrl ? (
              <img src={headshotPreviewUrl} style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto', display: 'block' }} alt="Headshot preview" />
            ) : (
              <>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--border-color)', margin: '0 auto 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                </div>
                <p className="text-muted text-sm">Click to upload a photo</p>
                <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>Square photo recommended. Max 2MB.</p>
              </>
            )}
            <input type="file" id="headshotFileInput" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleHeadshotFile(e.target.files[0]); }} />
          </div>
          {headshotPreviewUrl && (
            <button className="btn btn--outline btn--sm mt-1" onClick={() => { setHeadshotFile(null); setHeadshotPreviewUrl(''); }}>Remove</button>
          )}
        </div>

        <button className="btn btn--primary btn--full mt-2" onClick={saveProvider} disabled={loading}>
          {loading ? <><span style={{ visibility: 'hidden' }}>Save &amp; Continue</span><span className="spinner" style={{ position: 'absolute' }} /></> : 'Save & Continue'}
        </button>
      </div>
    </div>
  );
}
